import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useCallback } from 'react';
import localStore from 'store';
import {
  FIELDS_CONFIG,
  MAX_CONDITIONS_PER_FIELD,
} from '../constants/eligibilityEngine';
import {
  fetchProducts as fetchProductsAction,
  setSelectedProduct,
  setRules,
  setHasUnsavedChanges,
  updateRule,
  fetchRules as fetchRulesAction,
  setNextEffectiveFrom,
  publishRules,
  upsertRuleNote,
  fetchExternalHistory,
  fetchExternalHistoryByRuleKey,
} from '../data/eligibilityEngine/actions';
import { getProductType } from '../constants/productFamily';

const selectEligibilityEngine = state => state.data.eligibilityEngine;

export const useEligibilityProducts = () => {
  const {
    products, productsLoading, productsError, selectedProduct,
  } = useSelector(selectEligibilityEngine);
  return {
    products,
    productsLoading,
    productsError,
    selectedProduct,
  };
};

export const useEligibilityRules = () => {
  const {
    rules, isRulesLoading, rulesError, nextEffectiveFrom,
  } = useSelector(
    selectEligibilityEngine
  );
  return {
    rules,
    isRulesLoading,
    rulesError,
    nextEffectiveFrom,
  };
};

export const useEligibilityFields = () => {
  const { rules, fieldOverrides } = useSelector(selectEligibilityEngine);

  const fields = useMemo(() => {
    let merged = FIELDS_CONFIG;

    if (rules.length) {
      const rulesMap = {};
      rules.forEach(rule => {
        rulesMap[rule.name] = rule;
      });

      merged = FIELDS_CONFIG.map(fieldConfig => {
        const rule = rulesMap[fieldConfig.id];
        if (!rule) return fieldConfig;

        let parsedValue;
        try {
          parsedValue = JSON.parse(rule.value);
        } catch {
          parsedValue = rule.value;
        }

        if (
          parsedValue !== null
          && typeof parsedValue === 'object'
          && !Array.isArray(parsedValue)
        ) {
          return {
            ...fieldConfig,
            ...parsedValue,
            id: rule.id,
            actionOnNull: rule.actionOnNull ?? fieldConfig.actionOnNull,
            ...(fieldConfig.staticText !== undefined
              ? { staticText: fieldConfig.staticText }
              : {}),
          };
        }

        return {
          ...fieldConfig,
          value: parsedValue,
          id: rule.id,
          actionOnNull: rule.actionOnNull ?? fieldConfig.actionOnNull,
        };
      });
    }

    return merged.map(field => {
      const override = fieldOverrides[field.id];
      if (!override) return field;
      return { ...field, ...override };
    });
  }, [rules, fieldOverrides]);

  return fields;
};

export const useHasUnsavedChanges = () => {
  const { hasUnsavedChanges } = useSelector(selectEligibilityEngine);
  return hasUnsavedChanges;
};

/**
 * Returns the pending activation ISO date string for the currently selected
 * product, or null if there is no pending activation.
 */
export const usePendingActivation = () => {
  const { nextEffectiveFrom } = useSelector(selectEligibilityEngine);
  return nextEffectiveFrom || null;
};

export const useEligibilityTheme = () => {
  const { products, selectedProduct } = useEligibilityProducts();

  const selectedProductData = useMemo(
    () => products.find(p => p.categoryCode === selectedProduct),
    [products, selectedProduct]
  );

  const productType = useMemo(
    () => getProductType(selectedProductData?.displayName),
    [selectedProductData]
  );

  const themeClass = productType === 'seller-assurance' ? 'theme-green' : 'theme-blue';

  return {
    productType,
    themeClass,
    selectedProductData,
    selectedProduct,
  };
};

export const useEligibilityActions = () => {
  const dispatch = useDispatch();
  const fields = useEligibilityFields();
  const { selectedProduct } = useSelector(selectEligibilityEngine);

  const handleFetchProducts = useCallback(() => {
    dispatch(fetchProductsAction());
  }, [dispatch]);

  const handleFetchRules = useCallback(
    productId => {
      dispatch(fetchRulesAction(productId));
    },
    [dispatch]
  );

  const handleSetRules = useCallback(
    ruleList => {
      dispatch(setRules({ rules: ruleList, productCode: selectedProduct }));
    },
    [dispatch, selectedProduct]
  );

  const handleProductChange = useCallback(
    productId => {
      dispatch(setSelectedProduct(productId));
      localStore.set('eligibilitySelectedProduct', productId);
    },
    [dispatch]
  );

  const handleValueChange = useCallback(
    (ruleId, key, value) => {
      // If called as onValueChange(ruleId, fullValue) (no third arg),
      // treat `key` as either the full changes object or a JSON string for `value`.
      if (value === undefined && key !== undefined) {
        if (typeof key === 'object') {
          dispatch(updateRule({ ruleId, changes: key }));
          dispatch(setHasUnsavedChanges(true));
          return;
        }
        // If `key` is a string, we assume it's the JSON stringified `value` for the rule.
        if (typeof key === 'string') {
          dispatch(updateRule({ ruleId, changes: { value: key } }));
          dispatch(setHasUnsavedChanges(true));
          return;
        }
      }

      const currentField = fields.find(f => f.id === ruleId);
      if (!currentField) return;

      if (key === 'bucket') {
        const newBuckets = [...(currentField.buckets || [])];
        newBuckets[value.index] = {
          ...newBuckets[value.index],
          value: value.value,
        };
        dispatch(updateRule({ ruleId, changes: { buckets: newBuckets } }));
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      if (key === 'condition') {
        const newConditions = [...(currentField.conditions || [])];
        newConditions[value.index] = {
          ...newConditions[value.index],
          [value.key]: value.value,
        };
        dispatch(
          updateRule({
            ruleId,
            changes: { conditions: newConditions },
          })
        );
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      dispatch(updateRule({ ruleId, changes: { [key]: value } }));
      dispatch(setHasUnsavedChanges(true));
    },
    [dispatch, fields]
  );

  const handleIfNullChange = useCallback(
    (ruleId, value) => {
      dispatch(updateRule({ ruleId, changes: { actionOnNull: value } }));
      dispatch(setHasUnsavedChanges(true));
    },
    [dispatch]
  );

  const handleAddCondition = useCallback(
    ruleId => {
      const currentField = fields.find(f => f.id === ruleId);
      if (!currentField) return;

      const effectiveMax = currentField.maxConditions ?? MAX_CONDITIONS_PER_FIELD;

      let currentCount = 0;
      if (currentField.conditions && Array.isArray(currentField.conditions)) {
        currentCount = currentField.conditions.length;
      } else if (currentField.valueType === 'comparison_value') {
        currentCount = 1;
      }

      if (currentCount >= effectiveMax) return;

      if (currentField.conditions && Array.isArray(currentField.conditions)) {
        dispatch(
          updateRule({
            ruleId,
            changes: {
              conditions: [
                ...currentField.conditions,
                {
                  includeExclude: 'include',
                  comparison: 'gte',
                  field: currentField.label || '',
                  value: '',
                },
              ],
            },
          })
        );
        dispatch(setHasUnsavedChanges(true));
        return;
      }

      if (currentField.valueType === 'comparison_value') {
        dispatch(
          updateRule({
            ruleId,
            changes: {
              valueType: 'condition_builder',
              conditions: [
                {
                  includeExclude: currentField.includeExclude || 'include',
                  comparison: currentField.comparison || 'gte',
                  field: currentField.label || '',
                  value: currentField.comparisonValue || '',
                },
                {
                  includeExclude: 'include',
                  comparison: 'gte',
                  field: currentField.label || '',
                  value: '',
                },
              ],
            },
          })
        );
        dispatch(setHasUnsavedChanges(true));
      }
    },
    [dispatch, fields]
  );

  const handleRevert = useCallback(() => {
    // Reload rules from API to revert all changes
    if (selectedProduct) {
      dispatch(fetchRulesAction(selectedProduct));
    }
  }, [dispatch, selectedProduct]);

  const handlePublish = useCallback(
    ({ effectiveFrom }) => dispatch(publishRules({ effectiveFrom })),
    [dispatch]
  );

  const handleUpsertNote = useCallback(
    ({ productCode, ruleId, note }) => dispatch(upsertRuleNote({ productCode, ruleId, note })),
    [dispatch]
  );

  const handleClearPendingActivation = useCallback(
    () => dispatch(setNextEffectiveFrom(null)),
    [dispatch]
  );

  return {
    fetchProducts: handleFetchProducts,
    fetchRules: handleFetchRules,
    setRules: handleSetRules,
    onProductChange: handleProductChange,
    onValueChange: handleValueChange,
    onIfNullChange: handleIfNullChange,
    onAddCondition: handleAddCondition,
    onRevert: handleRevert,
    onPublish: handlePublish,
    upsertNote: handleUpsertNote,
    clearPendingActivation: handleClearPendingActivation,
  };
};

export const useStatusHistoryFetcher = () => {
  const dispatch = useDispatch();

  const fetchHistory = useCallback(
    async({
      productCode, ruleKey, from, to,
    }) => {
      if (ruleKey) {
        return dispatch(fetchExternalHistoryByRuleKey({
          productCode, ruleKey, from, to,
        }));
      }
      return dispatch(fetchExternalHistory({ productCode, from, to }));
    },
    [dispatch]
  );

  return { fetchHistory };
};
